import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, CalendarClock, ListChecks } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PriorityBadge, StatusSelect } from "./TaskBadges";
import { TaskFormDialog } from "./TaskFormDialog";
import { ConfirmDialog } from "./ConfirmDialog";
import { EmptyState } from "./AppLayout";
import { CommentBadge } from "./CommentsThread";

const Due = ({ t }) => (
  <span className={`inline-flex items-center gap-1.5 text-sm ${t.scaduta ? "text-[#E8173A] font-bold" : "text-slate-600"}`}>
    <CalendarClock className="h-3.5 w-3.5" />
    {fmtDate(t.scadenza)}
    {t.scaduta && <span className="text-[10px] uppercase tracking-wider bg-red-50 border border-red-200 rounded-full px-1.5">Scaduta</span>}
  </span>
);

import { ReservedLine } from "@/components/client/ReservedLine";
import { useSelection, SelectAll, SelectOne, BulkBar } from "@/components/core/BulkActions";

export const TaskTable = ({ tasks, onChanged, showClient = true, users = [], clients = [], clientId, emptyText, emptyAction }) => {
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);

  const changeStatus = async (t, stato) => {
    try {
      await api.patch(`/tasks/${t.id}/status`, { stato });
      toast.success("Stato aggiornato");
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/tasks/${del.id}`);
      toast.success("Task eliminata");
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDel(null);
  };

  const selection = useSelection();
  if (!tasks?.length) return <EmptyState icon={ListChecks} title="Nessuna task" text={emptyText || "Non ci sono task da mostrare."} action={emptyAction} testid="tasks-empty" />;

  return (
    <>
      <BulkBar tipo="task" selection={selection} onDone={() => onChanged?.()} />
      <Table data-testid="task-table">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-10"><SelectAll ids={tasks.map((t) => t.id)} selection={selection} testid="tasks-select-all" /></TableHead>
            <TableHead className="text-xs font-bold uppercase tracking-wider">Task</TableHead>
            {showClient && <TableHead className="text-xs font-bold uppercase tracking-wider">Cliente</TableHead>}
            <TableHead className="text-xs font-bold uppercase tracking-wider">Socio</TableHead>
            <TableHead className="text-xs font-bold uppercase tracking-wider">Scadenza</TableHead>
            <TableHead className="text-xs font-bold uppercase tracking-wider">Priorità</TableHead>
            <TableHead className="text-xs font-bold uppercase tracking-wider">Stato</TableHead>
            <TableHead className="w-20" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {tasks.map((t) => (
            <TableRow key={t.id} className="group" data-testid={`task-row-${t.id}`}>
              <TableCell><SelectOne id={t.id} selection={selection} testid={`task-select-${t.id}`} /></TableCell>
              <TableCell className="max-w-[340px]">
                <div className={`font-bold text-slate-900 flex items-center gap-2 ${t.stato === "completata" ? "line-through text-slate-400" : ""}`}>{t.titolo}<CommentBadge n={t.commenti} testid={`task-comments-${t.id}`} /></div>
                {t.descrizione && <div className="text-xs text-slate-500 truncate">{t.descrizione}</div>}
              </TableCell>
              {showClient && (
                <TableCell>
                  <Link to={`/clienti/${t.client_id}`} className="text-sm font-semibold text-[#0A1128] hover:text-[#E8173A]" data-testid={`task-client-link-${t.id}`}>{t.client_nome}</Link>
                  <div><ReservedLine clientId={t.client_id} testid={`task-row-reserved-${t.id}`} /></div>
                </TableCell>
              )}
              <TableCell className="text-sm text-slate-700">{t.assignee_nome || <span className="text-slate-400">—</span>}</TableCell>
              <TableCell><Due t={t} /></TableCell>
              <TableCell><PriorityBadge value={t.priorita} /></TableCell>
              <TableCell><StatusSelect task={t} onChange={changeStatus} /></TableCell>
              <TableCell>
                <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setEdit(t)} className="p-2 rounded-lg hover:bg-[#A6DAFC] text-[#0A1128]" data-testid={`task-edit-${t.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => setDel(t)} className="p-2 rounded-lg hover:bg-red-50 text-[#E8173A]" data-testid={`task-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TaskFormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} task={edit} users={users} clients={clients} clientId={clientId} onSaved={onChanged} onCommentsChange={onChanged} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare la task?"
        description={`La task «${del?.titolo}» verrà eliminata definitivamente.`} onConfirm={remove} />
    </>
  );
};
